import { useApiHealthStore } from './health'
import { HttpError, fetchWithRetry, type FetchRetryOptions } from './http'

export interface BocFxPoint {
  /** ISO YYYY-MM-DD (already in ISO from BoC). */
  date: string
  /** 1 USD = `value` CAD on that date. */
  value: number
}

export type BocErrorCode =
  | 'NETWORK'
  | 'TIMEOUT'
  | 'HTTP_4XX'
  | 'HTTP_5XX'
  | 'RATE_LIMITED'
  | 'ABORTED'
  | 'PARSE'

export class BocApiError extends Error {
  readonly code: BocErrorCode
  override readonly cause: unknown

  constructor(code: BocErrorCode, message: string, cause?: unknown) {
    super(message)
    this.name = 'BocApiError'
    this.code = code
    this.cause = cause
  }
}

export const BOC_VALET_BASE_URL = 'https://www.bankofcanada.ca/valet/observations/FXUSDCAD/json'

export interface BocValetClient {
  fetchFxUsdCadRange(startDateIso: string, endDateIso: string): Promise<BocFxPoint[]>
}

/**
 * Fetches the FXUSDCAD daily series within an inclusive date range. Returns
 * the published dates only — Canadian holidays and weekends are absent.
 * Observations with `v: null` are filtered out.
 */
export async function fetchFxUsdCadRange(
  startDateIso: string,
  endDateIso: string,
  options?: FetchRetryOptions,
): Promise<BocFxPoint[]> {
  const url = `${BOC_VALET_BASE_URL}?start_date=${startDateIso}&end_date=${endDateIso}`

  let response: Response
  try {
    response = await fetchWithRetry(url, options)
  } catch (err) {
    useApiHealthStore.getState().recordFailure('boc')
    throw asBocError(err)
  }

  let raw: unknown
  try {
    raw = await response.json()
  } catch (err) {
    useApiHealthStore.getState().recordFailure('boc')
    throw new BocApiError('PARSE', 'invalid JSON in BoC Valet response', err)
  }

  if (typeof raw !== 'object' || raw === null) {
    useApiHealthStore.getState().recordFailure('boc')
    throw new BocApiError('PARSE', 'expected JSON object from BoC Valet')
  }

  const observations = (raw as Record<string, unknown>)['observations']
  if (!Array.isArray(observations)) {
    useApiHealthStore.getState().recordFailure('boc')
    throw new BocApiError('PARSE', 'BoC Valet response missing "observations" array')
  }

  useApiHealthStore.getState().recordSuccess('boc')
  return observations
    .map(parseObservation)
    .filter((p): p is BocFxPoint => p !== null)
    .sort((a, b) => a.date.localeCompare(b.date))
}

function parseObservation(observation: unknown, index: number): BocFxPoint | null {
  if (typeof observation !== 'object' || observation === null) {
    throw new BocApiError('PARSE', `BoC observation ${index} is not an object`)
  }
  const record = observation as Record<string, unknown>
  const dateField = record['d']

  if (typeof dateField !== 'string') {
    throw new BocApiError('PARSE', `BoC observation ${index} missing string "d"`)
  }

  const fxField = record['FXUSDCAD']
  if (typeof fxField !== 'object' || fxField === null) {
    throw new BocApiError('PARSE', `BoC observation ${index} missing FXUSDCAD object`)
  }

  const valueField = (fxField as Record<string, unknown>)['v']
  if (valueField === null || valueField === undefined || valueField === '') {
    return null
  }
  if (typeof valueField !== 'string') {
    throw new BocApiError(
      'PARSE',
      `BoC observation ${index} has non-string FXUSDCAD.v`,
    )
  }

  const value = Number.parseFloat(valueField)
  if (!Number.isFinite(value) || value <= 0) {
    throw new BocApiError('PARSE', `BoC observation ${index} has invalid v="${valueField}"`)
  }

  return { date: dateField, value }
}

function asBocError(err: unknown): BocApiError {
  if (err instanceof HttpError) {
    return new BocApiError(err.code, `BoC Valet request failed: ${err.message}`, err)
  }
  return new BocApiError('NETWORK', err instanceof Error ? err.message : String(err), err)
}
